import { Location } from '@angular/common';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { catchError, throwError } from 'rxjs';
import { lireMaintenance } from '../models/maintenance.model';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../environments/environment';

/** `/login` et ses sous-pages (`/login/admin`), query string comprise. */
function estPageDeConnexion(chemin: string): boolean {
  const path = chemin.split(/[?#]/)[0];

  return path === '/login' || path.startsWith('/login/');
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const location = inject(Location);
  const auth = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const status = error.status ?? 0;

      // Coupure volontaire annoncée par l'API. Traitée AVANT Sentry : une maintenance d'une
      // heure produirait sinon un flot d'exceptions pour un incident qui n'en est pas un.
      const maintenance =
        status === 503 && req.url.startsWith(environment.apiBaseUrl)
          ? lireMaintenance(error.error)
          : null;
      if (maintenance) {
        auth.signalerMaintenance(maintenance);
        // Pas au démarrage : l'hydratation de session part AVANT la première navigation, et
        // les guards routent déjà sur /unavailable à partir du drapeau qu'on vient de poser.
        // L'intercepteur ne sert qu'au cas où la coupure tombe en PLEINE SESSION — c'est le
        // seul endroit qui voit ces 503-là, sans quoi l'utilisateur verrait ses cartes tomber
        // une à une sans jamais basculer sur la page de coupure.
        if (router.navigated && !router.url.startsWith('/unavailable')) {
          void router.navigate(['/unavailable']);
        }

        return throwError(() => error);
      }

      // On ne remonte à Sentry que les vraies pannes : 5xx + erreurs réseau (status 0).
      // Les 4xx (401/403/404/422...) sont du flux normal/métier => pas de bruit.
      if (status >= 500 || status === 0) {
        Sentry.captureException(error, {
          tags: { http_status: status },
          contexts: { api: { url: error.url ?? req.url, detail: error.error?.detail } },
        });
      }

      if (status === 401) {
        auth.clearSession();
        // Déjà sur une page de connexion, il n'y a nulle part où renvoyer. Deux 401 y arrivent
        // normalement : la session absente au démarrage, et un mauvais mot de passe sur
        // `/login/admin`. Sans ce test, l'un comme l'autre ramenaient de `/login/admin` à
        // `/login`, et la page administrateur était inatteignable. C'est l'adresse du
        // navigateur qui est lue : au démarrage, le routeur n'a encore rien navigué.
        if (!estPageDeConnexion(location.path())) {
          void router.navigate(['/login']);
        }
      }

      return throwError(() => error);
    }),
  );
};
